import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Database, Download, Loader2, Plus, RefreshCw, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MappingTable } from "./MappingTable";
import { RuleDialog } from "./RuleDialog";
import { ImportDialog } from "./ImportDialog";
import { downloadBlob, errMsg } from "./ui";

function NotConnected({ isSuper }) {
  const nav = useNavigate();
  return (
    <Card className="mx-auto mt-6 max-w-xl border-dashed border-slate-300 p-8 text-center shadow-none" data-testid="bs-not-connected">
      <Database className="mx-auto h-8 w-8 text-slate-300" />
      <h3 className="mt-3 font-heading text-base font-semibold text-slate-800">Connect the billing database</h3>
      <p className="mt-1 text-sm text-slate-500">Product Mapping edits the live <span className="font-mono">product_map</span> table your nightly tracked-items sync reads.
        {isSuper ? " Pick its MySQL connection in Settings to start." : " Ask a super admin to connect it."}</p>
      {isSuper && <Button className="mt-4" onClick={() => nav("/billing-sync/settings")} data-testid="bs-goto-settings">Open Billing Sync settings</Button>}
    </Card>
  );
}

function Stat({ label, value, hint, tone = "text-slate-900", onClick, active, testid }) {
  const C = onClick ? "button" : "div";
  return (
    <C type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
       className={`rounded-lg border bg-white px-4 py-3 text-left transition-colors ${active ? "border-amber-400 ring-1 ring-amber-300" : "border-slate-200"} ${onClick ? "hover:border-slate-300" : ""}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="truncate text-[11px] text-slate-500">{hint}</div>}
    </C>
  );
}

export function ProductMapping() {
  const [d, setD] = useState(null);
  const [cat, setCat] = useState(null);
  const [q, setQ] = usePersistedState("product-mapping.q", "");
  const [onlyWarn, setOnlyWarn] = usePersistedState("product-mapping.only-warn", false);
  const [dlg, setDlg] = useState(null);
  const [catBusy, setCatBusy] = useState(false);
  const load = useCallback(() => api.get("/billing-sync/mappings").then(({ data }) => setD(data))
    .catch((e) => { toast.error(errMsg(e, "Could not read the billing table")); setD((x) => x || { error: true }); }), []);
  const loadCat = useCallback((refresh = false) => {
    setCatBusy(true);
    return api.get("/billing-sync/products", { params: { refresh } }).then(({ data }) => { setCat(data); if (refresh) toast.success(`${data.products.length} products loaded from Stax Bill`); })
      .catch((e) => toast.error(errMsg(e, "Could not load the Stax Bill products"))).finally(() => setCatBusy(false));
  }, []);
  useEffect(() => { load(); loadCat(); }, [load, loadCat]);

  const ops = useMemo(() => ({ ...(d?.operators || {}), ...(d?.legacy_operators || {}) }), [d]);
  const byId = useMemo(() => Object.fromEntries((cat?.products || []).map((p) => [p.id, p])), [cat]);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (d?.rows || []).filter((r) => (!onlyWarn || r.warnings.length) && (!t || JSON.stringify([r.id, r.filters, r.products,
      r.products.map((p) => byId[p.id]?.name)]).toLowerCase().includes(t)));
  }, [d, q, onlyWarn, byId]);

  if (!d) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-slate-300" />;
  if (d.error) return <p className="mt-10 text-center text-sm text-red-600" data-testid="bs-mapping-error">The billing table could not be read. <button type="button" className="underline" onClick={load}>Try again</button></p>;
  if (!d.configured) return <NotConnected isSuper={d.is_super} />;
  const exportCsv = async () => {
    try { const { data } = await api.get("/billing-sync/mappings/export", { responseType: "blob" }); downloadBlob(data, `${d.table}_${new Date().toISOString().slice(0, 10)}.csv`); }
    catch (e) { toast.error("Could not export"); }
  };
  const del = async (r) => {
    try { await api.delete(`/billing-sync/mappings/${r.id}`, { data: r.base }); toast.success(`Rule #${r.id} deleted from the billing table`); }
    catch (e) { toast.error(errMsg(e, "Could not delete")); }
    setDlg(null); load();
  };
  const canImport = d.can.create || d.can.edit || d.can.delete;
  return (
    <div className="space-y-4" data-testid="bs-product-mapping">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-xs text-amber-900" data-testid="bs-live-banner">
        <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" /></span>
        <b>Live billing table</b> — saves write to <span className="font-mono">{d.table}</span> on {d.connection.name} ({d.connection.host}/{d.connection.database}) and bill from the next sync run.
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Rules" value={d.rows.length} hint="run lowest priority first" testid="bs-stat-rules" />
        <Stat label="With warnings" value={d.warning_count} tone={d.warning_count ? "text-amber-600" : "text-emerald-600"}
              hint={onlyWarn ? "showing only these — click to show all" : "click to show only these"} active={onlyWarn}
              onClick={() => setOnlyWarn(!onlyWarn)} testid="bs-stat-warnings" />
        <Stat label="Stax Bill products" value={cat ? cat.products.length : "…"} testid="bs-stat-products"
              hint={cat ? `4 plans · fetched ${new Date(cat.fetched_at).toLocaleString()}` : "loading…"} />
      </div>
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="relative w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rules, values, products…" className="h-9 pl-8" data-testid="bs-search" /></div>
          {onlyWarn && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"><AlertTriangle className="h-3 w-3" /> Warnings only</span>}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => loadCat(true)} disabled={catBusy} data-testid="bs-refresh-products">
              <RefreshCw className={`h-3.5 w-3.5 ${catBusy ? "animate-spin" : ""}`} /> Refresh products</Button>
            {d.can.export && <Button size="sm" variant="outline" onClick={exportCsv} data-testid="bs-export"><Download className="h-3.5 w-3.5" /> Export CSV</Button>}
            {canImport && <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "import" })} data-testid="bs-import"><Upload className="h-3.5 w-3.5" /> Import CSV</Button>}
            {d.can.create && <Button size="sm" onClick={() => setDlg({ kind: "rule" })} disabled={!cat} data-testid="bs-new-rule"><Plus className="h-3.5 w-3.5" /> New rule</Button>}
          </div>
        </div>
        <div className="max-h-[68vh] overflow-auto">
          <MappingTable rows={rows} ops={ops} byId={byId} can={{ ...d.can, edit: d.can.edit && !!cat }}
                        onEdit={(r) => setDlg({ kind: "rule", row: r })} onDelete={(r) => setDlg({ kind: "delete", row: r })} />
        </div>
      </Card>
      {dlg?.kind === "rule" && <RuleDialog row={dlg.row} meta={d} catalog={cat} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {dlg?.kind === "import" && <ImportDialog onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      <AlertDialog open={dlg?.kind === "delete"} onOpenChange={(o) => !o && setDlg(null)}>
        <AlertDialogContent data-testid="bs-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule #{dlg?.row?.id}?</AlertDialogTitle>
            <AlertDialogDescription>It is removed from the live billing table. Extensions it matched fall through to the next rule on the next sync run.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bs-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(dlg.row)} data-testid="bs-delete-confirm">Delete rule</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
